/**
 * Single source of truth for the B2B dashboard feature grid.
 *
 * Adding a feature to the dashboard means adding one entry here — the cards,
 * the plane grouping and the ordering all read from this map. Nothing else
 * needs to change.
 *
 * `key`     matches the :featureKey route param and (once the aggregate
 *           endpoint lands) the `key` field the API returns.
 * `plane`   which band the card renders in.
 * `unit`    the noun that follows the headline metric on the card.
 * `manage`  existing management page, `:slug` substituted at render time.
 *           null means the feature has no company-side management page —
 *           true for the Shoorah content features, which companies consume
 *           rather than operate.
 * `toggle`  field on the company record that enables/disables it, where one
 *           exists (see EnableFeatures.jsx).
 * `icon`    FEATURE_ICONS entry for the card, keyed by the same `key` as the
 *           feature itself. Deliberately not STAT_ICONS: that map is keyed by
 *           kind of metric and shared with every hub's stat cards, so it gave
 *           all six care programmes one icon and all three scans another.
 * `featured` shown as one of the three cards on its plane; everything else is
 *           reachable through that plane's "Select more" card.
 * `group`   heading the feature sits under inside the "Select more" dialog.
 *           Only used there — the cards and charts ignore it.
 */
import { FEATURE_ICONS } from '../component/common/designSystem';

export const PLANE = {
  PEOPLE: 'people',
  SHOORAH: 'shoorah',
  COMPANY: 'company'
};

/**
 * Plane accent colours, all existing shoorah tokens from
 * tailwind-overwrites.json. Deliberately all cool hues so they never collide
 * with the green/amber/red that carry feature status.
 */
export const PLANE_META = {
  [PLANE.PEOPLE]: {
    label: 'People',
    note: 'Who is here and are they engaged',
    accent: '#00BCD4' // shoorah-lightBlue
  },
  [PLANE.SHOORAH]: {
    label: 'Shoorah features',
    note: 'Wellbeing content included in your plan',
    accent: '#1F1F1F' // shoorah-active
  },
  [PLANE.COMPANY]: {
    label: 'Company features',
    note: 'Programmes you run for your people',
    accent: '#1F1F1F' // shoorah-primary
  }
};

export const FEATURE_STATUS = {
  ON: 'on',
  OFF: 'off',
  NEEDS_SETUP: 'needs_setup',
  NOT_ON_PLAN: 'not_on_plan'
};

/**
 * Headings inside the "Select more" dialog, in the order they render there.
 * A feature with no `group` falls to the bottom under FEATURE_GROUP.OTHER.
 */
export const FEATURE_GROUP = {
  PRACTICE: 'Content and practice',
  TRACKING: 'Tracking and tools',
  TESTING: 'Health testing',
  CARE: 'Care programmes',
  HABIT_CHANGE: 'Habit change',
  OTHER: 'Other'
};

export const FEATURE_GROUP_ORDER = [
  FEATURE_GROUP.PRACTICE,
  FEATURE_GROUP.TRACKING,
  FEATURE_GROUP.TESTING,
  FEATURE_GROUP.CARE,
  FEATURE_GROUP.HABIT_CHANGE,
  FEATURE_GROUP.OTHER
];

export const DASHBOARD_FEATURES = [
  // ---------- Shoorah features ----------
  {
    key: 'breathwork',
    name: 'Breathwork',
    plane: PLANE.SHOORAH,
    unit: 'sessions',
    icon: FEATURE_ICONS.breathwork,
    featured: true,
    group: FEATURE_GROUP.PRACTICE,
    manage: '/:slug/breathwork'
  },
  {
    key: 'shoorah_pods',
    name: 'Shoorah Pods',
    plane: PLANE.SHOORAH,
    unit: 'plays',
    icon: FEATURE_ICONS.shoorah_pods,
    group: FEATURE_GROUP.PRACTICE,
    manage: '/:slug/shoorah-pods'
  },
  {
    key: 'habit_tracker',
    name: 'Habit Tracker',
    plane: PLANE.SHOORAH,
    unit: 'habits active',
    icon: FEATURE_ICONS.habit_tracker,
    featured: true,
    group: FEATURE_GROUP.TRACKING,
    manage: '/:slug/habits'
  },
  {
    key: 'sleep',
    name: 'Sleep',
    plane: PLANE.SHOORAH,
    unit: 'logs',
    icon: FEATURE_ICONS.sleep,
    featured: true,
    group: FEATURE_GROUP.PRACTICE,
    manage: '/:slug/sleep'
  },
  {
    key: 'shuru',
    name: 'Shuru',
    plane: PLANE.SHOORAH,
    unit: 'conversations',
    icon: FEATURE_ICONS.shuru,
    group: FEATURE_GROUP.TRACKING,
    manage: '/:slug/shuru',
    toggle: 'shuru_usage'
  },
  {
    key: 'peap',
    name: 'PEAP',
    plane: PLANE.SHOORAH,
    unit: 'wellpoints issued',
    icon: FEATURE_ICONS.peap,
    // Demoted to "Select more" so Habit Tracker can take the third featured
    // slot on this plane — PEAP is still fully reachable, just not headline.
    group: FEATURE_GROUP.TRACKING,
    manage: '/:slug/peap-wellbank',
    toggle: 'peap_usage'
  },

  {
    key: 'meditation',
    name: 'Meditation',
    plane: PLANE.SHOORAH,
    unit: 'sessions',
    icon: FEATURE_ICONS.meditation,
    group: FEATURE_GROUP.PRACTICE,
    manage: '/:slug/meditation'
  },
  {
    // One card for both video libraries: the Health/NHS videos embedded from
    // Vimeo and the Shoorah Vids uploaded in the admin panel. They share a
    // collection and an audience, so the section reports them together and
    // splits them where it matters.
    key: 'shoorah_vids',
    name: 'Shoorah Vids',
    plane: PLANE.SHOORAH,
    unit: 'views',
    icon: FEATURE_ICONS.shoorah_vids,
    group: FEATURE_GROUP.PRACTICE,
    manage: '/:slug/shoorah-videos'
  },
  {
    // The Mood Log check-in — an emotion plus the context around it. Distinct
    // from the legacy slider moods that feed the main dashboard's positivity
    // widgets; the two records have nothing in common and are never combined.
    key: 'mood_tracking',
    name: 'Mood Tracking',
    plane: PLANE.SHOORAH,
    unit: 'moods logged',
    icon: FEATURE_ICONS.mood_tracking,
    group: FEATURE_GROUP.TRACKING,
    manage: '/:slug/mood'
  },
  {
    // The weighted questionnaires under Experience → Self Assessment. Two kinds
    // share the card: scored ones that return a percentage, and attachment ones
    // that return a style. Distinct from Insight Test, which is the separate
    // Shoorah Tests catalogue.
    key: 'self_assessment',
    name: 'Self-Assessment',
    plane: PLANE.SHOORAH,
    unit: 'assessments taken',
    icon: FEATURE_ICONS.self_assessment,
    group: FEATURE_GROUP.TESTING,
    manage: '/:slug/self-assessment'
  },
  {
    // The Vitall partner's home test kits. The section reports volume, stage
    // and how long each leg of the journey takes; what a test found never
    // leaves the partner's record.
    key: 'home_testing',
    name: 'Home Testing',
    plane: PLANE.SHOORAH,
    unit: 'kits ordered',
    icon: FEATURE_ICONS.home_testing,
    group: FEATURE_GROUP.TESTING,
    manage: '/:slug/home-testing'
  },

  /*
    The rest of the wellbeing catalogue. These are app-side features companies
    consume rather than operate, so `manage` is null until the feature detail
    template at /:slug/dashboard/f/:featureKey lands — the "Select more" dialog
    lists them and marks them as having no page yet rather than dead-ending on
    a click.
  */
  {
    key: 'guided_path',
    name: 'Guided Path',
    plane: PLANE.SHOORAH,
    unit: 'paths started',
    icon: FEATURE_ICONS.guided_path,
    group: FEATURE_GROUP.PRACTICE,
    // Both kinds of plan — AI and custom — live behind this one card, because
    // they are one collection with one boolean between them and a company
    // asking "is Guided Path landing?" means both.
    manage: '/:slug/guided-path'
  },
  {
    key: 'self_development',
    name: 'Self Development',
    plane: PLANE.SHOORAH,
    // Gratitude, Cleanse, Journal, Goals, Vision Board and Affirmations all
    // write an entry; "modules completed" described a course this never was.
    //
    // There is deliberately no separate Affirmation card. The Affirmations tool
    // inside this section already reports both halves of the feature: the ones
    // people write for themselves (`user_affirmations`) and plays of the
    // published catalogue (`trendings`, content_type AFFIRMATION) — which was
    // the whole of what the old standalone card counted.
    unit: 'entries written',
    icon: FEATURE_ICONS.self_development,
    group: FEATURE_GROUP.PRACTICE,
    manage: '/:slug/self-development'
  },
  {
    key: 'vision_board',
    name: 'Vision Board',
    plane: PLANE.SHOORAH,
    unit: 'boards created',
    icon: FEATURE_ICONS.vision_board,
    group: FEATURE_GROUP.TRACKING,
    // Vision Board is a tab inside Self Development now. The card stays — it
    // is still its own number on the dashboard — but managing it means opening
    // the section it lives in.
    manage: '/:slug/self-development/vision-board'
  },
  {
    key: 'game_tracking',
    name: 'Games',
    plane: PLANE.SHOORAH,
    // Four games — Chess, Word Search, Block Fill and You Quiz — each recording
    // a different kind of play. "games played" was the honest unit for none of
    // them: a chess game, a cleared level and a quiz attempt are not the same
    // event, so the card counts times played and the section splits it out.
    unit: 'times played',
    icon: FEATURE_ICONS.game_tracking,
    group: FEATURE_GROUP.TRACKING,
    manage: '/:slug/games'
  },
  {
    key: 'elite_mind',
    name: 'Elite Mind',
    plane: PLANE.SHOORAH,
    // The athlete side of Shoorah — sport profiles, the daily athlete journal
    // and injury/symptom/concussion reports. NOT the Elite Games quiz engine,
    // which is one of the four games behind the `game_tracking` card above; the
    // unit here used to say "games played", which pointed at the wrong feature
    // entirely.
    unit: 'journal entries',
    icon: FEATURE_ICONS.elite_mind,
    group: FEATURE_GROUP.TRACKING,
    manage: '/:slug/elite-mind'
  },
  {
    key: 'moneymind',
    name: 'MoneyMind',
    plane: PLANE.SHOORAH,
    unit: 'things logged',
    icon: FEATURE_ICONS.moneymind,
    group: FEATURE_GROUP.TRACKING,
    manage: '/:slug/moneymind'
  },
  {
    key: 'insight_test',
    name: 'Insight Test',
    plane: PLANE.SHOORAH,
    unit: 'tests taken',
    icon: FEATURE_ICONS.insight_test,
    group: FEATURE_GROUP.TESTING,
    manage: null
  },
  {
    key: 'skin_scan',
    name: 'Skin Scan',
    plane: PLANE.SHOORAH,
    unit: 'scans',
    icon: FEATURE_ICONS.skin_scan,
    group: FEATURE_GROUP.TESTING,
    manage: '/:slug/scans/skin'
  },
  {
    key: 'teeth_scan',
    name: 'Teeth Scan',
    plane: PLANE.SHOORAH,
    unit: 'scans',
    icon: FEATURE_ICONS.teeth_scan,
    group: FEATURE_GROUP.TESTING,
    manage: '/:slug/scans/teeth'
  },
  {
    key: 'age_scan',
    name: 'Age Scan',
    plane: PLANE.SHOORAH,
    unit: 'scans',
    icon: FEATURE_ICONS.age_scan,
    group: FEATURE_GROUP.TESTING,
    // The three scan cards all land inside the one Scans section, each on its
    // own tab — they are one feature wearing three hats, and reading them side
    // by side is the point.
    manage: '/:slug/scans/age'
  },
  // The six CARE cards land in two sections: four in Care Programmes and two in
  // Reproductive Health, each card on its own tab. The split is not cosmetic —
  // Reproductive Health can be switched off for a customer independently, and
  // the backend refuses to serve one section's programme through the other's
  // endpoint. Neither section has a users list; enrolment is itself a health
  // disclosure, so no figure in either is ever attached to a name.
  {
    key: 'glp_1',
    name: 'GLP-1',
    plane: PLANE.SHOORAH,
    unit: 'enrolled',
    icon: FEATURE_ICONS.glp_1,
    group: FEATURE_GROUP.CARE,
    manage: '/:slug/care/glp-1'
  },
  {
    key: 'pause',
    name: 'Pause',
    plane: PLANE.SHOORAH,
    unit: 'enrolled',
    icon: FEATURE_ICONS.pause,
    group: FEATURE_GROUP.CARE,
    manage: '/:slug/care/pause'
  },
  {
    key: 'peptide_care',
    name: 'Peptide Care',
    plane: PLANE.SHOORAH,
    unit: 'enrolled',
    icon: FEATURE_ICONS.peptide_care,
    group: FEATURE_GROUP.CARE,
    manage: '/:slug/care/peptide'
  },
  {
    key: 'menstrual_care',
    name: 'Menstrual Care',
    plane: PLANE.SHOORAH,
    unit: 'enrolled',
    icon: FEATURE_ICONS.menstrual_care,
    group: FEATURE_GROUP.CARE,
    manage: '/:slug/reproductive-health/menstrual'
  },
  {
    key: 'fertility_care',
    name: 'Fertility Care',
    plane: PLANE.SHOORAH,
    unit: 'enrolled',
    icon: FEATURE_ICONS.fertility_care,
    group: FEATURE_GROUP.CARE,
    manage: '/:slug/reproductive-health/fertility'
  },
  {
    key: 'fibromyalgia_care',
    name: 'Fibromyalgia Care',
    plane: PLANE.SHOORAH,
    unit: 'enrolled',
    icon: FEATURE_ICONS.fibromyalgia_care,
    group: FEATURE_GROUP.CARE,
    manage: '/:slug/care/fibromyalgia'
  },
  {
    key: 'trigger_trace',
    name: 'Trigger Trace',
    plane: PLANE.SHOORAH,
    unit: 'triggers logged',
    icon: FEATURE_ICONS.trigger_trace,
    group: FEATURE_GROUP.HABIT_CHANGE,
    manage: '/:slug/trigger-trace'
  },
  // Two cards, one section, two tabs. Both land on their own page inside Quit
  // Programmes, which is aggregate-only: unlike every other `manage` target in
  // this file, there is no per-person view behind either of these.
  {
    key: 'last_puff',
    name: 'Last Puff',
    plane: PLANE.SHOORAH,
    unit: 'quit plans',
    icon: FEATURE_ICONS.last_puff,
    group: FEATURE_GROUP.HABIT_CHANGE,
    manage: '/:slug/cessation/last-puff'
  },
  {
    key: 'last_drink',
    name: 'Last Drink',
    plane: PLANE.SHOORAH,
    unit: 'quit plans',
    icon: FEATURE_ICONS.last_drink,
    group: FEATURE_GROUP.HABIT_CHANGE,
    manage: '/:slug/cessation/last-drink'
  },

  // ---------- Company features ----------
  {
    key: 'benefits',
    name: 'Benefits',
    plane: PLANE.COMPANY,
    unit: 'claimed',
    icon: FEATURE_ICONS.benefits,
    featured: true,
    manage: '/:slug/benefits'
  },
  {
    key: 'perks',
    name: 'Perks',
    plane: PLANE.COMPANY,
    unit: 'enrolled',
    icon: FEATURE_ICONS.perks,
    featured: true,
    // The umbrella over Instant Support (EAP), Salary Sacrifice, Health Scheme
    // and Custom Perks — the same grouping the super-admin panel calls
    // Offerings. As with Marketplace, this card is the analytics entry point;
    // /:slug/offering (manage the programmes) stays where the sidebar points.
    manage: '/:slug/perks'
  },
  {
    key: 'challenges',
    name: 'Challenges',
    plane: PLANE.COMPANY,
    unit: 'running',
    icon: FEATURE_ICONS.challenges,
    featured: true,
    // The insights dashboards, not the authoring flow: like Marketplace and
    // Perks, this card is an analytics entry point, and /:slug/challenges
    // (create + manage) stays where the sidebar points.
    manage: '/:slug/challenges/insights'
  },
  {
    key: 'shoutouts',
    name: 'Shoutouts',
    plane: PLANE.COMPANY,
    unit: 'sent',
    icon: FEATURE_ICONS.shoutouts,
    manage: '/:slug/shoutouts'
  },
  {
    key: 'marketplace',
    name: 'Marketplace',
    plane: PLANE.COMPANY,
    unit: 'orders',
    icon: FEATURE_ICONS.marketplace,
    // The insights dashboards, not the shop: this card is an analytics entry
    // point like Sleep and Breathwork, and /:slug/marketplace (browse + cart)
    // stays where the sidebar points.
    manage: '/:slug/marketplace/insights'
  },
  {
    key: 'badges',
    name: 'Badges',
    plane: PLANE.COMPANY,
    unit: 'earned',
    icon: FEATURE_ICONS.badges,
    manage: '/:slug/badges'
  },
  {
    key: 'pulse_surveys',
    name: 'Pulse Surveys',
    plane: PLANE.COMPANY,
    unit: 'responses',
    icon: FEATURE_ICONS.pulse_surveys,
    manage: '/:slug/pulse-survey'
  }
  /*
    Custom EAP, Salary Sacrifice and Health Scheme used to be three separate
    cards here. They are now the three programmes inside Perks — matching the
    super-admin panel, where all three live under Offerings — so they are
    reached through the Perks card above rather than duplicated beside it.
    Their management pages keep their own routes (/:slug/instant-support,
    /:slug/salary-sacrifice, /:slug/health-scheme) and are still linked from
    /:slug/offering, which is where the sidebar's Perks entry points.
  */
];

export const getFeaturesByPlane = (plane) =>
  DASHBOARD_FEATURES.filter((feature) => feature.plane === plane);

export const getFeatureByKey = (key) =>
  DASHBOARD_FEATURES.find((feature) => feature.key === key) || null;

/** Substitutes :slug into a feature's management route. */
export const resolveManageRoute = (feature, slug) =>
  feature?.manage && slug ? feature.manage.replace(':slug', slug) : null;

/** The three cards shown for a plane. */
export const getFeaturedByPlane = (plane) =>
  getFeaturesByPlane(plane).filter((feature) => feature.featured);

/** Everything else on that plane, reached via the "Select more" card. */
export const getMoreByPlane = (plane) =>
  getFeaturesByPlane(plane).filter((feature) => !feature.featured);

/**
 * Substring match over the things an admin would actually type: the feature
 * name and its group heading. Case- and punctuation-insensitive, so "glp1"
 * finds GLP-1 and "self dev" finds Self Development.
 */
const normalise = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export const matchesFeatureQuery = (feature, query) => {
  const needle = normalise(query);
  if (!needle) return true;
  const haystack = `${normalise(feature.name)} ${normalise(feature.group)}`;
  // The separator-free copy is what makes "glp1" find GLP-1, whose normalised
  // name is "glp 1".
  const compact = haystack.replace(/ /g, '');
  // Every whitespace-separated token must appear, so "care fertility" matches.
  return needle.split(' ').every((token) => haystack.includes(token) || compact.includes(token));
};

/**
 * Features bucketed under their group heading. Empty groups are dropped, so
 * the caller can filter first and render this straight out.
 *
 * `order` is a parameter because the super-admin dashboard groups its cards by
 * a different vocabulary (see utils/adminDashboardFeatures.js). A feature whose
 * group is absent from the order would otherwise be silently dropped from the
 * picker rather than shown — invisible to the admin and hard to diagnose, so
 * anything unplaced falls to OTHER instead.
 */
export const groupFeatures = (features, order = FEATURE_GROUP_ORDER) => {
  const known = new Set(order);
  const headings = known.has(FEATURE_GROUP.OTHER) ? order : [...order, FEATURE_GROUP.OTHER];

  return headings
    .map((group) => ({
      group,
      features: features.filter((feature) => {
        const own = feature.group || FEATURE_GROUP.OTHER;
        return (known.has(own) ? own : FEATURE_GROUP.OTHER) === group;
      })
    }))
    .filter((section) => section.features.length > 0);
};
